import { DestroyRef, ElementRef, inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';

/** Default max-width applied to popover panels when no explicit width strategy is set */
export const POPOVER_DEFAULT_MAX_WIDTH = '250px';

export interface PopoverContainerRef {
  container: HTMLElement | null;
}

/**
 * Popover width mode:
 * - 'match-trigger-width': syncs the popover width to the trigger element width
 * - A CSS value (e.g. '300px'): sets that as max-width
 * - undefined: applies the default max-width
 */
export type PopoverWidthMode = 'match-trigger-width' | (string & {}) | undefined;

/** Custom event name used to coordinate closing multiple dropdowns */
const CLOSE_DROPDOWNS_EVENT = 'ocp-close-dropdowns';

/** Counter for generating unique instance IDs across all dropdown types */
let instanceCounter = 0;

/**
 * Service to coordinate dropdown behavior across multiple dropdown instances.
 * Handles closing other dropdowns when one opens and provides unique instance IDs.
 *
 * Usage:
 * ```ts
 * private readonly dropdownCoordination = inject(DropdownCoordinationService);
 * private readonly destroyRef = inject(DestroyRef);
 * readonly instanceId = this.dropdownCoordination.generateInstanceId();
 *
 * constructor() {
 *   this.dropdownCoordination.registerCloseHandler(
 *     this.elementRef,
 *     () => this.popover()?.hide(),
 *     this.destroyRef
 *   );
 * }
 *
 * togglePopover(event: Event): void {
 *   this.dropdownCoordination.notifyOpen(this.elementRef);
 *   this.popover()?.toggle(event);
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class DropdownCoordinationService {
  private readonly document = inject(DOCUMENT);

  /**
   * Generate a unique instance ID for option element IDs.
   * Call this once per component instance and store the result.
   */
  generateInstanceId(): number {
    return ++instanceCounter;
  }

  /**
   * Register a handler to close this dropdown when another opens.
   * Automatically cleans up on destroy.
   *
   * @param elementRef The component's ElementRef to identify the source
   * @param closeCallback Function to call when this dropdown should close
   * @param destroyRef The component's DestroyRef for cleanup
   */
  registerCloseHandler(
    elementRef: ElementRef<HTMLElement>,
    closeCallback: () => void,
    destroyRef: DestroyRef
  ): void {
    const subscription = this.createCloseHandler(elementRef, closeCallback);
    destroyRef.onDestroy(subscription.unsubscribe);
  }

  /**
   * Create a close handler and return an object with an unsubscribe method.
   * Use this with effect's onCleanup to avoid injecting DestroyRef.
   */
  createCloseHandler(
    elementRef: ElementRef<HTMLElement>,
    closeCallback: () => void
  ): { unsubscribe: () => void } {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{ source: Element }>;
      if (customEvent.detail.source !== elementRef.nativeElement) {
        closeCallback();
      }
    };

    this.document.addEventListener(CLOSE_DROPDOWNS_EVENT, handler);
    return {
      unsubscribe: () => this.document.removeEventListener(CLOSE_DROPDOWNS_EVENT, handler),
    };
  }

  /**
   * Notify other dropdowns that this one is opening.
   * Call this before toggling the popover.
   *
   * @param elementRef The component's ElementRef to identify the source
   */
  notifyOpen(elementRef: ElementRef<HTMLElement>): void {
    this.document.dispatchEvent(
      new CustomEvent(CLOSE_DROPDOWNS_EVENT, {
        detail: { source: elementRef.nativeElement },
      })
    );
  }
}

/**
 * Handle common trigger button keyboard events.
 * Returns true if the event was handled (Enter or Space).
 */
export function handleTriggerKeydown(event: KeyboardEvent): boolean {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    return true;
  }
  return false;
}

/**
 * Calculate the next option index with wrapping.
 */
export function getNextOptionIndex(currentIndex: number, totalOptions: number): number {
  return (currentIndex + 1) % totalOptions;
}

/**
 * Calculate the previous option index with wrapping.
 */
export function getPreviousOptionIndex(currentIndex: number, totalOptions: number): number {
  return (currentIndex - 1 + totalOptions) % totalOptions;
}

/**
 * Sync the popover panel width with the host element width.
 * Call this in onPopoverShow() to ensure the dropdown panel is properly sized.
 *
 * @param mode - 'match-trigger-width' to sync width with the trigger, a CSS value for custom max-width,
 *               or undefined to apply the default max-width.
 */
export function syncPopoverWidth(
  elementRef: ElementRef<HTMLElement>,
  popover: PopoverContainerRef | undefined,
  mode?: PopoverWidthMode
): void {
  const container = popover?.container;
  if (!container) return;

  if (mode === 'match-trigger-width') {
    container.style.maxWidth = '';
    container.style.width = `${elementRef.nativeElement.offsetWidth}px`;
  } else {
    container.style.width = '';
    container.style.maxWidth = mode || POPOVER_DEFAULT_MAX_WIDTH;
  }
}
